import { createFileRoute } from "@tanstack/react-router";
export const Route = createFileRoute("/user/_layout/crm/calendar")({
  component: CrmCalendarPage,
});

import { useState, useMemo, useCallback } from "react";

import { useAuthStore } from "@/stores/useAuthStore";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CrmCalendarView } from "@/components/crm/calendar/CrmCalendarView";
import { MeetingFormSheet } from "@/components/crm/forms/MeetingFormSheet";
import { CallFormSheet } from "@/components/crm/forms/CallFormSheet";
import { CrmTaskFormSheet } from "@/components/crm/forms/CrmTaskFormSheet";
import {
  useFetchMeetings,
  useMeetingMutations,
} from "@/hooks/api/crm/useMeetings";
import { useFetchCalls, useCallMutations } from "@/hooks/api/crm/useCalls";
import {
  useFetchCrmTasks,
  useCrmTaskMutations,
} from "@/hooks/api/crm/useCrmTasks";
import { EmailFormSheet } from "@/components/crm/forms/EmailFormSheet";
import { useFetchEmails, useEmailMutations } from "@/hooks/api/crm/useEmails";
import { useFetchAccounts } from "@/hooks/api/crm/useAccounts";
import { AssigneeFilter, ALL_TEAM_MEMBERS_ID } from "@/components/crm/AssigneeFilter";
import { dayjs, toCalendarLocalDate } from "@/lib/date-utils";
import { useCrmPermissions } from "@/lib/crm-permissions";
import type {
  CalendarEvent,
  CalendarEventType,
  CrmMeeting,
  CrmCall,
  CrmTask,
  CrmMeetingPayload,
  CrmCallPayload,
  CrmTaskPayload,
  CrmEmail,
  CrmEmailPayload,
} from "@/types/crm";

const noop = async () => {};

// Local refinement of CalendarEvent that ties each `type` to its concrete
// resource, so the view/edit sheets receive typed records without casts.
interface CalendarEventBase {
  id: string;
  title: string;
  start: Date;
  end: Date;
}

type TypedCalendarEvent =
  | (CalendarEventBase & { type: "meeting"; resource: CrmMeeting })
  | (CalendarEventBase & { type: "call"; resource: CrmCall })
  | (CalendarEventBase & { type: "task"; resource: CrmTask })
  | (CalendarEventBase & { type: "email"; resource: CrmEmail });

function buildDateRange(range: Date[] | { start: Date; end: Date }) {
  if (Array.isArray(range)) {
    return {
      start: dayjs(range[0]).startOf("day").toDate(),
      end: dayjs(range[range.length - 1])
        .endOf("day")
        .toDate(),
    };
  }
  return {
    start: dayjs(range.start).toDate(),
    end: dayjs(range.end).toDate(),
  };
}

function CrmCalendarPage() {
  const currentUser = useAuthStore((s) => s.user);

  const [showMeetings, setShowMeetings] = useState(true);
  const [showCalls, setShowCalls] = useState(true);
  const [showTasks, setShowTasks] = useState(true);
  const [showEmails, setShowEmails] = useState(true);
  // undefined = user hasn't explicitly picked anything yet.
  const [sharedUserId, setSharedUserId] = useState<number | undefined>(
    undefined
  );
  const [selectedAccountId, setSelectedAccountId] = useState<string>("");

  // currentUser loads asynchronously (no persisted/hydrated-on-init auth
  // state), so it's not available on first render. Fall back to it here
  // (derived at render time, not synced via effect) so both the selector's
  // display and the query filter reflect "self" as soon as it's known,
  // without ever clobbering an explicit user selection.
  const effectiveSharedUserId = sharedUserId ?? currentUser?.user_id;

  const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>(
    () => ({
      start: dayjs().startOf("month").toDate(),
      end: dayjs().endOf("month").toDate(),
    })
  );

  const { canModify } = useCrmPermissions();
  const [viewingEvent, setViewingEvent] = useState<TypedCalendarEvent | null>(
    null
  );
  const [isViewSheetOpen, setIsViewSheetOpen] = useState(false);
  const [createSheet, setCreateSheet] = useState<{
    open: boolean;
    type: CalendarEventType | null;
    date: Date | null;
  }>({ open: false, type: null, date: null });
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);

  // Selecting "All Team Members" (sentinel id) clears the filter entirely;
  // otherwise scope to whoever is effectively selected (self, by default).
  const assignedTo =
    effectiveSharedUserId === ALL_TEAM_MEMBERS_ID
      ? undefined
      : effectiveSharedUserId
        ? String(effectiveSharedUserId)
        : undefined;
  const after = dayjs(dateRange.start).format("YYYY-MM-DDTHH:mm:ss");
  const before = dayjs(dateRange.end).format("YYYY-MM-DDTHH:mm:ss");
  const dueDateAfter = dayjs(dateRange.start).format("YYYY-MM-DD");
  const dueDateBefore = dayjs(dateRange.end).format("YYYY-MM-DD");

  // Selecting an account shows all its activities regardless of assignee,
  // since account-linked activities (e.g. a shared/house account) are often unassigned.
  const activityFilterParams = selectedAccountId
    ? { parent_account: selectedAccountId }
    : assignedTo
      ? { assigned_to: assignedTo }
      : {};

  // Don't fetch at all until we know what to filter by (an account, an
  // explicit selection, or the current user having loaded) — otherwise the
  // very first render would fire one unfiltered "everyone" request before
  // currentUser resolves.
  const filterReady = !!selectedAccountId || effectiveSharedUserId !== undefined;

  const meetingsQuery = useFetchMeetings(
    {
      page_size: 100,
      start_datetime_after: after,
      start_datetime_before: before,
      ...activityFilterParams,
    },
    { enabled: filterReady }
  );
  const callsQuery = useFetchCalls(
    {
      page_size: 100,
      start_datetime_after: after,
      start_datetime_before: before,
      ...activityFilterParams,
    },
    { enabled: filterReady }
  );
  const tasksQuery = useFetchCrmTasks(
    {
      page_size: 100,
      due_date_after: dueDateAfter,
      due_date_before: dueDateBefore,
      ...activityFilterParams,
    },
    { enabled: filterReady }
  );
  const emailsQuery = useFetchEmails(
    {
      page_size: 100,
      email_datetime_after: after,
      email_datetime_before: before,
      ...activityFilterParams,
    },
    { enabled: filterReady }
  );

  const meetings = useMemo(
    () => meetingsQuery.data?.results ?? [],
    [meetingsQuery.data]
  );
  const calls = useMemo(() => callsQuery.data?.results ?? [], [callsQuery.data]);
  const crmTasks = useMemo(
    () => tasksQuery.data?.results ?? [],
    [tasksQuery.data]
  );
  const emails = useMemo(
    () => emailsQuery.data?.results ?? [],
    [emailsQuery.data]
  );

  const accountsQuery = useFetchAccounts({ page_size: 100 });
  const accounts = useMemo(
    () => accountsQuery.data?.results ?? [],
    [accountsQuery.data]
  );

  const { createMeeting, updateMeeting } = useMeetingMutations();
  const { createCall, updateCall } = useCallMutations();
  const { createCrmTask, updateCrmTask } = useCrmTaskMutations();
  const { createEmail, updateEmail } = useEmailMutations();

  const meetingMutLoading = createMeeting.isPending || updateMeeting.isPending;
  const callMutLoading = createCall.isPending || updateCall.isPending;
  const taskMutLoading = createCrmTask.isPending || updateCrmTask.isPending;
  const emailMutLoading = createEmail.isPending || updateEmail.isPending;

  const handleRangeChange = useCallback(
    (range: Date[] | { start: Date; end: Date }) => {
      setDateRange(buildDateRange(range));
    },
    []
  );

  const allEvents = useMemo<TypedCalendarEvent[]>(() => {
    const events: TypedCalendarEvent[] = [];

    if (showMeetings) {
      meetings.forEach((m: CrmMeeting) => {
        events.push({
          id: m.id,
          title: m.subject,
          start: toCalendarLocalDate(m.start_datetime)!,
          end: toCalendarLocalDate(m.end_datetime)!,
          type: "meeting",
          resource: m,
        });
      });
    }

    if (showTasks) {
      crmTasks.forEach((t: CrmTask) => {
        if (!t.due_date) return;
        const start = toCalendarLocalDate(dayjs(t.due_date).startOf("day"))!;
        const end = toCalendarLocalDate(dayjs(t.due_date).endOf("day"))!;
        events.push({
          id: String(t.id),
          title: t.title,
          start,
          end,
          type: "task",
          resource: t,
        });
      });
    }

    if (showCalls) {
      calls.forEach((c: CrmCall) => {
        const start = toCalendarLocalDate(c.start_datetime ?? c.created_at)!;
        const end = new Date(
          start.getTime() + (c.duration_minutes ?? 30) * 60 * 1000
        );
        events.push({
          id: c.id,
          title: c.subject,
          start,
          end,
          type: "call",
          resource: c,
        });
      });
    }

    if (showEmails) {
      emails.forEach((e: CrmEmail) => {
        if (!e.email_datetime) return;
        const start = toCalendarLocalDate(e.email_datetime)!;
        const end = new Date(start.getTime() + 30 * 60 * 1000);
        events.push({
          id: e.id,
          title: e.subject,
          start,
          end,
          type: "email",
          resource: e,
        });
      });
    }

    return events;
  }, [meetings, calls, crmTasks, emails, showMeetings, showCalls, showTasks, showEmails]);

  const isFetching =
    meetingsQuery.isFetching ||
    callsQuery.isFetching ||
    tasksQuery.isFetching ||
    emailsQuery.isFetching;

  const handleEventClick = (event: CalendarEvent) => {
    // The calendar view hands back one of the objects supplied via `events`;
    // re-resolve it against allEvents to recover its typed resource.
    const typed = allEvents.find(
      (e) => e.type === event.type && e.id === event.id
    );
    if (!typed) return;
    setViewingEvent(typed);
    setIsViewSheetOpen(true);
  };

  const closeViewSheet = () => {
    setIsViewSheetOpen(false);
    setViewingEvent(null);
  };

  const handleEditClick = () => {
    setIsViewSheetOpen(false);
    setIsEditSheetOpen(true);
  };

  // The read-only sheets render their Edit button only when onEditClick is
  // provided, so withholding it hides the action for records the user may not
  // modify (the tables gate the same way via canModify).
  const editClickIfAllowed =
    viewingEvent && canModify(viewingEvent.resource) ? handleEditClick : undefined;

  const closeEditSheet = useCallback(() => {
    setIsEditSheetOpen(false);
    setViewingEvent(null);
  }, []);

  const handleCreateRequest = useCallback(
    (type: CalendarEventType, date?: Date) => {
      setCreateSheet({ open: true, type, date: date ?? null });
    },
    []
  );

  const closeCreateSheet = useCallback(() => {
    setCreateSheet({ open: false, type: null, date: null });
  }, []);

  const handleMeetingCreated = useCallback(
    async (data: CrmMeetingPayload) => {
      await createMeeting.mutateAsync(data);
      closeCreateSheet();
    },
    [createMeeting, closeCreateSheet]
  );

  const handleCallCreated = useCallback(
    async (data: CrmCallPayload) => {
      await createCall.mutateAsync(data);
      closeCreateSheet();
    },
    [createCall, closeCreateSheet]
  );

  const handleTaskCreated = useCallback(
    async (data: CrmTaskPayload) => {
      await createCrmTask.mutateAsync(data);
      closeCreateSheet();
    },
    [createCrmTask, closeCreateSheet]
  );

  const handleEmailCreated = useCallback(
    async (data: CrmEmailPayload) => {
      await createEmail.mutateAsync(data);
      closeCreateSheet();
    },
    [createEmail, closeCreateSheet]
  );

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="flex flex-1 flex-col space-y-4 p-6 pt-0">
        <PageHeader
          title="Calendar"
          description="View all meetings, calls, tasks, and emails in one place."
          isLoading={isFetching}
        />

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">
              Show:
            </span>
            <Button
              variant={showMeetings ? "default" : "outline"}
              size="sm"
              onClick={() => setShowMeetings((p) => !p)}
              className={
                showMeetings
                  ? "bg-blue-200 hover:bg-blue-300 text-blue-800"
                  : ""
              }
            >
              Meetings
            </Button>
            <Button
              variant={showCalls ? "default" : "outline"}
              size="sm"
              onClick={() => setShowCalls((p) => !p)}
              className={
                showCalls
                  ? "bg-green-200 hover:bg-green-300 text-green-800"
                  : ""
              }
            >
              Calls
            </Button>
            <Button
              variant={showTasks ? "default" : "outline"}
              size="sm"
              onClick={() => setShowTasks((p) => !p)}
              className={
                showTasks
                  ? "bg-orange-200 hover:bg-orange-300 text-orange-900"
                  : ""
              }
            >
              Tasks
            </Button>
            <Button
              variant={showEmails ? "default" : "outline"}
              size="sm"
              onClick={() => setShowEmails((p) => !p)}
              className={
                showEmails
                  ? "bg-purple-200 hover:bg-purple-300 text-purple-800"
                  : ""
              }
            >
              Emails
            </Button>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <span className="text-sm font-medium text-muted-foreground">
              Account:
            </span>
            <Select
              value={selectedAccountId || "__none__"}
              onValueChange={(v) =>
                setSelectedAccountId(v === "__none__" ? "" : v)
              }
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All accounts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">All accounts</SelectItem>
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={String(a.id)}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">
              Shared View:
            </span>
            <div className={selectedAccountId ? "pointer-events-none opacity-50" : ""}>
              <AssigneeFilter value={effectiveSharedUserId} onChange={setSharedUserId} allowAll />
            </div>
          </div>
        </div>

        <CrmCalendarView
          events={allEvents}
          loading={isFetching}
          onEventClick={handleEventClick}
          onRangeChange={handleRangeChange}
          onCreateRequest={handleCreateRequest}
        />

        {viewingEvent?.type === "meeting" && (
          <MeetingFormSheet
            isOpen={isViewSheetOpen}
            isViewOnly
            onClose={closeViewSheet}
            onCreate={noop}
            onEdit={noop}
            editingMeeting={viewingEvent.resource}
            isLoading={false}
            onEditClick={editClickIfAllowed}
          />
        )}
        {viewingEvent?.type === "call" && (
          <CallFormSheet
            isOpen={isViewSheetOpen}
            isViewOnly
            onClose={closeViewSheet}
            onCreate={noop}
            onEdit={noop}
            editingCall={viewingEvent.resource}
            isLoading={false}
            onEditClick={editClickIfAllowed}
          />
        )}
        {viewingEvent?.type === "task" && (
          <CrmTaskFormSheet
            isOpen={isViewSheetOpen}
            isViewOnly
            onClose={closeViewSheet}
            onCreate={noop}
            onEdit={noop}
            editingTask={viewingEvent.resource}
            isLoading={false}
            onEditClick={editClickIfAllowed}
          />
        )}
        {viewingEvent?.type === "email" && (
          <EmailFormSheet
            isOpen={isViewSheetOpen}
            isViewOnly
            onClose={closeViewSheet}
            onCreate={noop}
            onEdit={noop}
            editingEmail={viewingEvent.resource}
            isLoading={false}
            onEditClick={editClickIfAllowed}
          />
        )}

        {viewingEvent?.type === "meeting" && (
          <MeetingFormSheet
            isOpen={isEditSheetOpen}
            onClose={closeEditSheet}
            onCreate={noop}
            onEdit={async (id, data) => {
              await updateMeeting.mutateAsync({ id, payload: data });
              closeEditSheet();
            }}
            editingMeeting={viewingEvent.resource}
            isLoading={meetingMutLoading}
          />
        )}
        {viewingEvent?.type === "call" && (
          <CallFormSheet
            isOpen={isEditSheetOpen}
            onClose={closeEditSheet}
            onCreate={noop}
            onEdit={async (id, data) => {
              await updateCall.mutateAsync({ id, payload: data });
              closeEditSheet();
            }}
            editingCall={viewingEvent.resource}
            isLoading={callMutLoading}
          />
        )}
        {viewingEvent?.type === "task" && (
          <CrmTaskFormSheet
            isOpen={isEditSheetOpen}
            onClose={closeEditSheet}
            onCreate={noop}
            onEdit={async (id, data) => {
              await updateCrmTask.mutateAsync({ id, payload: data });
              closeEditSheet();
            }}
            editingTask={viewingEvent.resource}
            isLoading={taskMutLoading}
          />
        )}
        {viewingEvent?.type === "email" && (
          <EmailFormSheet
            isOpen={isEditSheetOpen}
            onClose={closeEditSheet}
            onCreate={noop}
            onEdit={async (id, data) => {
              await updateEmail.mutateAsync({ id, payload: data });
              closeEditSheet();
            }}
            editingEmail={viewingEvent.resource}
            isLoading={emailMutLoading}
          />
        )}

        <MeetingFormSheet
          isOpen={createSheet.open && createSheet.type === "meeting"}
          onClose={closeCreateSheet}
          onCreate={handleMeetingCreated}
          onEdit={noop}
          isLoading={meetingMutLoading}
          defaultDate={createSheet.date ?? undefined}
        />
        <CallFormSheet
          isOpen={createSheet.open && createSheet.type === "call"}
          onClose={closeCreateSheet}
          onCreate={handleCallCreated}
          onEdit={noop}
          isLoading={callMutLoading}
          defaultDate={createSheet.date ?? undefined}
        />
        <CrmTaskFormSheet
          isOpen={createSheet.open && createSheet.type === "task"}
          onClose={closeCreateSheet}
          onCreate={handleTaskCreated}
          onEdit={noop}
          isLoading={taskMutLoading}
          defaultDate={createSheet.date ?? undefined}
        />
        <EmailFormSheet
          isOpen={createSheet.open && createSheet.type === "email"}
          onClose={closeCreateSheet}
          onCreate={handleEmailCreated}
          onEdit={noop}
          isLoading={emailMutLoading}
          defaultDate={createSheet.date ?? undefined}
        />
      </div>
    </SidebarInset>
  );
}
